import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";

// Lazy load pages for code splitting
const CarListingPage = lazy(() => import("./pages/CarListingPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminRefresh = lazy(() => import("./pages/AdminRefresh"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const queryClient = new QueryClient();

// Wrapper component to apply Thai font class based on language
function LanguageWrapper({ children }: { children: React.ReactNode }) {
  const { language } = useLanguage();
  return (
    <div className={language === 'th' ? 'lang-th' : ''}>
      {children}
    </div>
  );
}

// TransShift V1
const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="transshift-theme">
      <LanguageProvider>
        <LanguageWrapper>
          <ComparisonProvider>
            <FavoritesProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <Suspense fallback={<div className="min-h-screen bg-background" />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/car/:slug" element={<CarListingPage />} />
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/admin/refresh" element={<AdminRefresh />} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </TooltipProvider>
            </FavoritesProvider>
          </ComparisonProvider>
        </LanguageWrapper>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
